import { useState } from 'react';
import { useIntl } from 'react-intl';
import { IconCustomMenuDown, IconCustomMenuUp } from '@rossum/ui/icons/tabler';
import {
  Button,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  SxProps,
  Theme,
  Typography,
} from '@rossum/ui/material';
import { FilterOperator } from '../../mql/operators';
import { getOperatorLocalisationKey } from '../helpers';

export type FilterOperatorsProps<T extends FilterOperator> = {
  headerName?: string;
  operators: T[];
  operator: T;
  onOperatorChange: (operator: T) => void;
};

export const operatorWrapperStyles: SxProps<Theme> = {
  width: '100%',
  flexDirection: 'row',
  justifyContent: 'space-between',
};

export const FilterOperators = <T extends FilterOperator>({
  operators,
  headerName,
  operator,
  onOperatorChange,
}: FilterOperatorsProps<T>) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const selectedOperatorKey = getOperatorLocalisationKey(operator);

  const isOpen = !!anchorEl;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {headerName ? (
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', textWrap: 'nowrap' }}
        >
          {headerName}
        </Typography>
      ) : null}
      <Menu
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            elevation: 8,
          },
        }}
      >
        {operators.map(o => {
          const operatorKey = getOperatorLocalisationKey(o);
          return operatorKey ? (
            <MenuItem
              key={o.value}
              onClick={() => {
                setAnchorEl(null);
                onOperatorChange(o);
              }}
              data-cy={`all-documents-operator-${o.value}-for-${headerName}`}
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: `containers.filtering.operators.${operatorKey}`,
                })}
              </Typography>
            </MenuItem>
          ) : null;
        })}
      </Menu>
      {selectedOperatorKey ? (
        <Button
          color="secondary"
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{ px: 0.5, py: 0, fontWeight: 'bold' }}
        >
          {intl.formatMessage({
            id: `containers.filtering.operators.${selectedOperatorKey}`,
          })}
          <SvgIcon fontSize="large">
            {isOpen ? <IconCustomMenuUp /> : <IconCustomMenuDown />}
          </SvgIcon>
        </Button>
      ) : null}
    </Stack>
  );
};
